import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Subscription } from '../models/User';
import { useAuth } from './AuthContext';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { isSubscriptionActive } from '../constants/subscriptions';

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: Error | null;
  refreshSubscription: () => Promise<void>;
  isSubscribed: boolean;
  isPremium: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscription = async () => {
    if (!user) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      setSubscription(userData?.subscription || null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch subscription'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Set up real-time subscription listener
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (doc) => {
        const userData = doc.data();
        setSubscription(userData?.subscription || null);
        setIsLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err : new Error('Failed to listen to subscription changes'));
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const refreshSubscription = async () => {
    setIsLoading(true);
    await fetchSubscription();
  };

  const derived = useMemo(() => {
    const active = subscription ? isSubscriptionActive(subscription.status) : false;
    return {
      isSubscribed: active,
      isPremium: active && subscription?.tier === 'premium',
    };
  }, [subscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isLoading,
        error,
        refreshSubscription,
        ...derived,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 